<script>
import useVuelidate from '@vuelidate/core';
import {required, minValue, maxValue} from '@vuelidate/validators';

/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView';
import GaFilter from '@components/v2/filter/GaFilter.vue';
import AkInputPercentage from '@components/v2/input/AkInputPercentage.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';
import AkDialog from '@components/general/AkDialog.vue';
import AkDialogConfirm from '@components/general/AkDialogConfirm.vue';
import AkDatatable from '@components/v2/general/AkDatatable.vue';

import GaFilterOwner from '@components/v2/filter/sidebarFilters/GaFilterOwner.vue';
import GaFilterAccommodation from '@components/v2/filter/sidebarFilters/GaFilterAccommodation.vue';
import GaFilterMarket from '@components/v2/filter/sidebarFilters/GaFilterMarket.vue';
import GaFilterTiers from '@components/v2/filter/sidebarFilters/GaFilterTiers.vue';
import GaFilterOffer from '@components/v2/filter/sidebarFilters/GaFilterOffer.vue';
import GaFilterTags from '@components/v2/filter/sidebarFilters/GaFilterTags.vue';
import GaFilterHouseKeepers from '@components/v2/filter/sidebarFilters/GaFilterHouseKeepers.vue';
import GaFilterChargeDeCompte from '@components/v2/filter/sidebarFilters/GaFilterChargeDeCompte.vue';
import GaFilterAccommodationType from '@components/v2/filter/sidebarFilters/GaFilterAccommodationType.vue';
import GaFilterAccommodationTypologie from '@components/v2/filter/sidebarFilters/GaFilterAccommodationTypologie.vue';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import markupService from '@services/markupService';

export default {
  components: {
    GaView,
    GaFilter,
    AkInputPercentage,
    AkDropdown,
    AkCalendar,
    AkButtonValidationV2,
    AkFormSubmitted,
    AkDialog,
    AkDialogConfirm,
    AkDatatable,
  },
  mixins: [messageMixin],
  head() {
    return {
      title: 'Markup en masse',
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      form: {
        percent: {required, minValue: minValue(0), maxValue: maxValue(100)},
        dateBasis: {required},
        dateStart: {required},
        dateEnd: {
          required,
          minValue: (value) => !this.form.dateStart || !value || new Date(value) >= new Date(this.form.dateStart),
        },
      },
    };
  },
  data() {
    return {
      filter: {
        ownerIds: [],
        accommodationIds: [],
        accommodationTypes: [],
        accommodationTypologies: [],
        mandatOffers: [],
        marcheIds: [],
        tagIds: [],
        housekeeperIds: [],
        chargeDeCompteIds: [],
        clusters: [],
      },
      form: {
        percent: null,
        dateBasis: 'SEJOUR',
        dateStart: null,
        dateEnd: null,
      },
      dateBasisOptions: [
        {value: 'RESERVATION', label: 'Dates de réservation'},
        {value: 'SEJOUR', label: 'Dates de séjour'},
      ],
      conflicts: [],
      submitted: false,
    };
  },
  computed: {
    filterComponents() {
      return [
        {component: GaFilterOwner, filterProperty: 'ownerIds', showIcon: true, label: 'Hôte'},
        {component: GaFilterMarket, filterProperty: 'marcheIds', showIcon: true, label: 'Marché'},
        {component: GaFilterAccommodation, filterProperty: 'accommodationIds', showIcon: true, label: 'Logement'},
        {component: GaFilterTiers, filterProperty: 'clusters', showIcon: true, label: 'Tiers'},
        {
          component: GaFilterAccommodationType,
          filterProperty: 'accommodationTypes',
          showIcon: true,
          label: 'Type de logement',
        },
        {component: GaFilterTags, filterProperty: 'tagIds', showIcon: true, label: 'Étiquettes'},
        {
          component: GaFilterAccommodationTypologie,
          filterProperty: 'accommodationTypologies',
          showIcon: true,
          label: 'Type de résidence',
        },
        {component: GaFilterHouseKeepers, filterProperty: 'housekeeperIds', showIcon: true, label: 'Concierge'},
        {component: GaFilterOffer, filterProperty: 'mandatOffers', showIcon: true, label: 'Formule'},
        {
          component: GaFilterChargeDeCompte,
          filterProperty: 'chargeDeCompteIds',
          showIcon: true,
          label: 'Chargé de compte',
        },
      ];
    },
    isFilterEmpty() {
      return Object.values(this.filter).every((value) => !Array.isArray(value) || value.length === 0);
    },
  },
  methods: {
    resetForm() {
      this.form.percent = null;
      this.form.dateBasis = 'SEJOUR';
      this.form.dateStart = null;
      this.form.dateEnd = null;
      this.submitted = false;
      this.v$.$reset();
    },
    apply(mergeStrategy, btnRef) {
      if (mergeStrategy === null) {
        this.submitted = true;
        this.v$.$touch();
        if (this.v$.$invalid) {
          this.$refs[btnRef].init();
          return;
        }
        if (this.isFilterEmpty) {
          this.$refs.confirmEmptyFilter
            .confirm()
            .then(() => this.submitBatch(mergeStrategy, btnRef))
            .catch(() => this.$refs[btnRef].init());
          return;
        }
      }
      this.submitBatch(mergeStrategy, btnRef);
    },
    submitBatch(mergeStrategy, btnRef) {
      markupService
        .applyBatch(
          this.filter,
          this.form.percent,
          this.form.dateBasis,
          this.form.dateStart,
          this.form.dateEnd,
          mergeStrategy,
        )
        .then(() => {
          this.$refs[btnRef].init();
          this.$refs.dialogConflict?.hide();
          this.addSuccess('Mark-up appliqué, synchronisation des prix Airbnb en cours');
          this.resetForm();
        })
        .catch((e) => {
          this.$refs[btnRef].init();
          if (e.response?.status === 409 && Array.isArray(e.response.data)) {
            this.conflicts = e.response.data;
            this.$nextTick(() => this.$refs.dialogConflict.show());
          } else {
            this.addError(e.response?.data?.error ?? "L'application du mark-up a échoué");
          }
        });
    },
    force() {
      this.apply('FORCE', 'btnForce');
    },
    ignore() {
      this.apply('IGNORE', 'btnIgnore');
    },
    cancelConflict() {
      this.$refs.dialogConflict.hide();
      this.resetForm();
    },
  },
};
</script>

<template>
  <GaView title="Markup en masse">
    <template #content>
      <div class="flex flex-col p-2 gap-4 [&_#filterButton]:!h-[53px]">
        <GaFilter v-model:filter="filter" :filter-components="filterComponents" :show-cadre="false">
          <template #inlineFilters>
            <AkFormSubmitted :submitted="submitted" reference="form" class="contents">
              <AkInputPercentage
                v-model="form.percent"
                label="Mark-up (%)"
                :max="9999"
                :fraction-digits="3"
                :validator="v$.form.percent" />
              <AkDropdown
                v-model="form.dateBasis"
                label="Base de la période"
                :options="dateBasisOptions"
                :validator="v$.form.dateBasis" />
              <AkCalendar
                v-model="form.dateStart"
                label="Date de début"
                :min-date="new Date()"
                :validator="v$.form.dateStart" />
              <AkCalendar
                v-model="form.dateEnd"
                label="Date de fin"
                :min-date="form.dateStart"
                :validator="v$.form.dateEnd" />
            </AkFormSubmitted>
          </template>
        </GaFilter>
        <div class="flex justify-end">
          <AkButtonValidationV2
            class="font-medium !px-6"
            ref="btnApply"
            label="Appliquer"
            :little="false"
            @action="apply(null, 'btnApply')" />
        </div>
      </div>
    </template>

    <template #extra>
      <AkDialogConfirm
        ref="confirmEmptyFilter"
        label="Aucun filtre sélectionné : le mark-up sera appliqué à tous les logements. Continuer ?" />

      <AkDialog ref="dialogConflict" title="Conflit détecté" :show-cancel-button="false" :show-validate-button="false">
        <p class="text-sm text-grey-600 mb-4">Les logements suivants ont déjà un mark-up spécifique actif :</p>
        <AkDatatable :value="conflicts" :loading="false">
          <Column header="Logement">
            <template #body="slotProps">
              {{ slotProps.data.accommodationName || '#' + slotProps.data.accommodationId }}
            </template>
          </Column>
          <Column header="Mark-up existant">
            <template #body="slotProps">
              {{ slotProps.data.existingPercent != null ? slotProps.data.existingPercent * 100 + ' %' : '—' }}
            </template>
          </Column>
        </AkDatatable>
        <div class="flex justify-end gap-2 mt-6">
          <span class="btn-secondary mr-auto font-medium px-5" @click="cancelConflict">Annuler</span>
          <AkButtonValidationV2
            ref="btnIgnore"
            label="Exclure ces logements"
            class="!px-5 font-medium"
            :little="false"
            @action="ignore" />
          <AkButtonValidationV2
            ref="btnForce"
            label="Inclure ces logements"
            class="!px-5 font-medium"
            :little="false"
            @action="force" />
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
